import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import * as Sentry from '@sentry/react';
import './i18n';
import './index.css';
import App from './App';
import { AuthProvider } from './context/AuthContext';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import ErrorFallback from './components/common/ErrorFallback';
import { gateTransaction, tracesSampler } from './utils/tracingConsent';

// Initialize GlitchTip (Sentry-compatible) error monitoring before
// React mounts so the ErrorBoundary below can report unhandled errors.
// Disabled when DSN env var is unset (local dev / tests).
const glitchtipDsn = import.meta.env.VITE_GLITCHTIP_DSN;
if (glitchtipDsn) {
  Sentry.init({
    dsn: glitchtipDsn,
    environment: import.meta.env.VITE_ENV || 'dev',
    release: import.meta.env.VITE_VCS_REF || undefined,
    // Performance tracing runs only after analytics consent; see
    // utils/tracingConsent.ts. Errors are sent regardless of consent.
    tracesSampler,
    beforeSendTransaction: gateTransaction,
    autoSessionTracking: false,
    // Tie any request id attached to a thrown error to the Sentry event so
    // support can correlate the user-facing reference id to the captured
    // crash. The ErrorFallback surfaces the same id in the UI.
    beforeSend: (event, hint) => {
      const err = hint?.originalException as { requestId?: string } | undefined;
      if (err?.requestId) {
        event.tags = { ...event.tags, request_id: err.requestId };
      }
      return event;
    },
  });
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error, resetError, eventId }) => (
        <ErrorFallback error={error} resetError={resetError} eventId={eventId} />
      )}
    >
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <App />
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);

// Register service worker for PWA support
serviceWorkerRegistration.register({
  onUpdate: (registration) => {
    // Notify user about new version
    if (registration.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
  },
  onSuccess: () => {
    console.log('Roosting is available offline.');
  },
});
